import React, {
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { cn } from "../utils/cn";
import { recordTable } from "../components/tables/table-presentation";
import { LoaderCircle, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Button as ShadcnButton } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useProjects } from "../contexts/ProjectContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { useAuth } from "../contexts/AuthContext";
import CreateProjectForm from "../components/CreateProjectForm";
import { projectsApi } from "../services/rag-service";
import { organizationApi } from "../services/organization-service";
import { useTranslation } from "react-i18next";
import { getIntlLocale } from "../utils/language";
import { useProjectsQuery } from "../features/projects/hooks/useProjectsQuery";
import {
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useUpdateProjectMutation,
} from "../features/projects/hooks/useProjectMutations";
import ProjectDetailsSheet from "../features/projects/components/ProjectDetailsSheet";
import ProjectAddMemberDialog from "../features/projects/components/ProjectAddMemberDialog";
import ProjectChangeAccessDialog from "../features/projects/components/ProjectChangeAccessDialog";
import ProjectRemoveMemberDialog from "../features/projects/components/ProjectRemoveMemberDialog";
import ProjectDeleteDialog, {
  isValidProjectDependencies,
} from "../features/projects/components/ProjectDeleteDialog";

const getAccessLevels = (t) => [
  {
    value: "admin",
    label: t("aiCatalog.admin"),
  },
  {
    value: "modifier",
    label: t("aiCatalog.editor"),
  },
  {
    value: "viewer",
    label: t("aiCatalog.viewer"),
  },
];

// Function to map API access levels to UI display
const mapApiAccessLevelToUI = (apiAccessLevel) => {
  if (apiAccessLevel === "modifier") {
    return "editor";
  }
  return apiAccessLevel;
};

// Function to map UI access levels to API
const mapUIAccessLevelToAPI = (uiAccessLevel) => {
  if (uiAccessLevel === "editor") {
    return "modifier";
  }
  return uiAccessLevel;
};

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const canManageProject = (project) =>
  Boolean(
    project &&
    (project.isOwner === true ||
      project.accessLevel === "owner" ||
      project.accessLevel === "admin"),
  );

const UUID_PATTERN =
  /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

const normalizeIdentityId = (value) => {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) ? String(value) : null;
  }

  if (typeof value !== "string") return null;
  const normalized = value.trim();
  if (!normalized) return null;
  return UUID_PATTERN.test(normalized) ? normalized.toLowerCase() : normalized;
};

const normalizeIdentityEmail = (value) =>
  typeof value === "string" && value.trim()
    ? value.trim().toLowerCase()
    : null;

const isSameUserIdentity = (member, user) => {
  const memberId = normalizeIdentityId(member?.user_id);
  const userId = normalizeIdentityId(user?.id);

  if (memberId && userId) return memberId === userId;

  const memberEmail = normalizeIdentityEmail(member?.email);
  const userEmail = normalizeIdentityEmail(user?.email);
  return Boolean(memberEmail && userEmail && memberEmail === userEmail);
};

const isProtectedOrganizationRole = (role) => {
  const normalizedRole =
    typeof role === "string" ? role.trim().toLowerCase() : null;
  return normalizedRole === "owner" || normalizedRole === "admin";
};

const emptyOrganizationProtection = (requestKey, status) => ({
  requestKey,
  status,
  userIds: new Set(),
  emails: new Set(),
  fallbackEmails: new Set(),
});

const EMPTY_PROJECTS = [];

const getAccessLevelLabel = (level, t) => {
  // Transform API level to UI level for display
  const uiLevel = mapApiAccessLevelToUI(level);
  const accessLevels = getAccessLevels(t);
  const accessLevel =
    accessLevels.find((l) => l.value === level) ||
    accessLevels.find((l) => l.value === uiLevel);

  return accessLevel?.label || level || t("common.unknown");
};

const ProjectsManagement = () => {
  const { shareProject, updateShareAccess } = useProjects();
  const projectsQuery = useProjectsQuery();
  const projects = projectsQuery.data ?? EMPTY_PROJECTS;
  const { mutateAsync: createProject } = useCreateProjectMutation();
  const { mutateAsync: updateProject } = useUpdateProjectMutation();
  const { mutateAsync: deleteProject } = useDeleteProjectMutation();
  const { currentOrganization, initialized: organizationInitialized } =
    useOrganization();
  const { currentUser } = useAuth();
  const isOrgOwner = currentOrganization?.role_id === "owner";
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const identityScope =
    organizationInitialized === true && currentUser?.id
      ? `${currentUser.id}:${currentOrganization?.id ?? "personal"}`
      : null;
  const organizationProtectionRequestKey = useMemo(
    () => ({ identityScope, organizationId: currentOrganization?.id ?? null }),
    [currentOrganization?.id, identityScope],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProjectScope, setSelectedProjectScope] = useState(null);
  const selectedProject = useMemo(
    () =>
      selectedProjectScope === identityScope
        ? (projects.find((project) => project.id === selectedProjectId) ?? null)
        : null,
    [identityScope, projects, selectedProjectId, selectedProjectScope],
  );
  const canManageSelectedProjectMembers =
    canManageProject(selectedProject) || isOrgOwner;
  const [createFormOpen, setCreateFormOpen] = useState(false);

  const [organizationMemberProtection, setOrganizationMemberProtection] =
    useState(() => emptyOrganizationProtection(null, "idle"));

  // Project members state
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [membersPagination, setMembersPagination] = useState({
    page: 1,
    size: 10,
    total: 0,
    pages: 1,
  });
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [projectDeleteDialogOpen, setProjectDeleteDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // New state for dependency checking
  const [dependenciesLoading, setDependenciesLoading] = useState(false);
  const [dependencies, setDependencies] = useState(null);
  const [deleteDependencyError, setDeleteDependencyError] = useState("");
  const [deleteMutationError, setDeleteMutationError] = useState("");
  const [forceDelete, setForceDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Member management states
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const restoreAddMemberFocusRef = useRef(false);
  const [accessLevelDialogOpen, setAccessLevelDialogOpen] = useState(false);
  const [newAccessLevel, setNewAccessLevel] = useState("");
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberAccessLevel, setNewMemberAccessLevel] = useState("viewer");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // New state for editing project
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [editProjectName, setEditProjectName] = useState("");
  const [editProjectDescription, setEditProjectDescription] = useState("");
  const [editProjectError, setEditProjectError] = useState("");
  const [editProjectPending, setEditProjectPending] = useState(false);
  const editSubmissionRef = useRef(false);
  const detailsSessionGenerationRef = useRef(0);
  const deleteRequestGenerationRef = useRef(0);
  const deleteSubmissionRef = useRef(null);
  const deleteSessionRef = useRef(null);
  const deleteTriggerRef = useRef(null);
  const deleteRestoreFocusRef = useRef(true);
  const memberRequestGenerationRef = useRef(0);
  const organizationProtectionGenerationRef = useRef(0);
  const memberSubmissionRef = useRef(null);
  const accessSessionRef = useRef(null);
  const removeMemberSessionRef = useRef(null);
  const restoreRemoveMemberFocusRef = useRef(null);
  const restoreAccessMemberFocusRef = useRef(null);
  const selectedProjectIdRef = useRef(null);
  const createProjectTriggerRef = useRef(null);
  const projectsListFocusRef = useRef(null);
  const canonicalProjectsRef = useRef(projects);
  const [detailsTriggerElement, setDetailsTriggerElement] = useState(null);
  const [deleteFallbackFocusElement, setDeleteFallbackFocusElement] =
    useState(null);
  const identityScopeRef = useRef(undefined);
  const activeIdentityScopeRef = useRef(identityScope);
  useLayoutEffect(() => {
    activeIdentityScopeRef.current = identityScope;
  }, [identityScope]);
  useLayoutEffect(() => {
    canonicalProjectsRef.current = projects;
  }, [projects]);

  // Function to fetch project dependencies
  const fetchProjectDependencies = useCallback(async (projectId) => {
    if (!projectId) return null;
    return projectsApi.getProjectDependencies(projectId);
  }, []);

  const invalidateMemberRequests = useCallback(() => {
    memberRequestGenerationRef.current += 1;
  }, []);

  const resetMemberState = useCallback(() => {
    removeMemberSessionRef.current = null;
    restoreRemoveMemberFocusRef.current = null;
    accessSessionRef.current = null;
    restoreAccessMemberFocusRef.current = null;
    restoreAddMemberFocusRef.current = false;
    setNewMemberEmail("");
    setNewMemberAccessLevel("viewer");
    invalidateMemberRequests();
    setMembers([]);
    setMembersLoading(false);
    setMembersError("");
    setMembersPagination({
      page: 1,
      size: 10,
      total: 0,
      pages: 1,
    });
  }, [invalidateMemberRequests, setNewMemberEmail, setNewMemberAccessLevel]);

  // Retain the service contract while isolating every project/page/retry session.
  const fetchProjectMembers = useCallback(async () => {
    const projectId = selectedProjectIdRef.current;
    if (!projectId) return;

    const requestScope = activeIdentityScopeRef.current;
    const requestedPage = membersPagination.page;
    const requestGeneration = memberRequestGenerationRef.current + 1;
    memberRequestGenerationRef.current = requestGeneration;
    setMembersLoading(true);
    setMembersError("");

    const isCurrentRequest = () =>
      memberRequestGenerationRef.current === requestGeneration &&
      selectedProjectIdRef.current === projectId &&
      activeIdentityScopeRef.current === requestScope;

    try {
      const result = await projectsApi.getProjectMembers(projectId, {
        page: requestedPage,
        limit: membersPagination.size || 10,
      });

      if (!isCurrentRequest()) return;
      setMembers(result.items || []);
      setMembersPagination((prev) => ({
        page: result.page || requestedPage || 1,
        total: result.total || 0,
        pages: result.pages || 1,
        size: prev.size || 10,
      }));
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error("Error fetching project members:", error);
      setMembersError(error.message || t("projects.failedToLoadTeamMembers"));
      setMembers([]);
    } finally {
      if (isCurrentRequest()) setMembersLoading(false);
    }
  }, [membersPagination.page, membersPagination.size, t]);

  // Resolve backend-protected organization roles for only the active identity scope.
  useEffect(() => {
    const requestGeneration = organizationProtectionGenerationRef.current + 1;
    organizationProtectionGenerationRef.current = requestGeneration;
    const requestScope = identityScope;
    const requestKey = organizationProtectionRequestKey;
    const organizationId = normalizeIdentityId(currentOrganization?.id);
    let active = true;

    if (!currentOrganization?.id) {
      return () => {
        active = false;
      };
    }

    if (!organizationId || !requestScope) {
      return () => {
        active = false;
      };
    }

    const isCurrentRequest = () =>
      active &&
      organizationProtectionGenerationRef.current === requestGeneration &&
      activeIdentityScopeRef.current === requestScope;

    const fetchProtectedOrganizationMembers = async () => {
      try {
        const response = await organizationApi.getOrganizationMembers(
          currentOrganization.id,
        );
        if (!isCurrentRequest()) return;
        if (!Array.isArray(response.data)) {
          throw new Error("Invalid organization members response");
        }

        const userIds = new Set();
        const emails = new Set();
        const fallbackEmails = new Set();
        response.data.forEach((member) => {
          if (!isProtectedOrganizationRole(member?.role_id)) return;

          const userId = normalizeIdentityId(member?.user_id);
          const email = normalizeIdentityEmail(member?.user?.email);
          if (userId) userIds.add(userId);
          if (email) emails.add(email);
          if (email && !userId) fallbackEmails.add(email);
        });
        setOrganizationMemberProtection({
          requestKey,
          status: "ready",
          userIds,
          emails,
          fallbackEmails,
        });
      } catch (error) {
        if (!isCurrentRequest()) return;
        console.error("Error fetching organization members:", error);
        setOrganizationMemberProtection(
          emptyOrganizationProtection(requestKey, "error"),
        );
      }
    };
    fetchProtectedOrganizationMembers();

    return () => {
      active = false;
    };
  }, [
    currentOrganization?.id,
    identityScope,
    organizationProtectionRequestKey,
  ]);

  const organizationProtectionReady =
    !currentOrganization?.id ||
    (organizationMemberProtection.requestKey ===
      organizationProtectionRequestKey &&
      organizationMemberProtection.status === "ready");

  const isProtectedOrganizationMember = (member) => {
    if (!currentOrganization?.id || !organizationProtectionReady) {
      return Boolean(currentOrganization?.id);
    }

    const memberId = normalizeIdentityId(member?.user_id);
    const memberEmail = normalizeIdentityEmail(member?.email);
    if (memberId && organizationMemberProtection.userIds.has(memberId)) {
      return true;
    }

    const protectedEmails = memberId
      ? organizationMemberProtection.fallbackEmails
      : organizationMemberProtection.emails;
    return Boolean(memberEmail && protectedEmails.has(memberEmail));
  };

  const isMemberProtectedFromAccessChange = (member) =>
    isSameUserIdentity(member, currentUser) ||
    isProtectedOrganizationMember(member);

  const isMemberProtectedFromRemoval = (member) =>
    isSameUserIdentity(member, currentUser) ||
    isProtectedOrganizationMember(member);

  const canChangeMemberAccess = (member) =>
    canManageSelectedProjectMembers &&
    !isMemberProtectedFromAccessChange(member);

  const canRemoveMember = (member) =>
    canManageSelectedProjectMembers && !isMemberProtectedFromRemoval(member);

  const isCurrentMemberSession = (projectId, requestScope) =>
    Boolean(
      projectId &&
        requestScope &&
        selectedProject?.id === projectId &&
        selectedProjectIdRef.current === projectId &&
        selectedProjectScope === requestScope &&
        identityScope === requestScope &&
        activeIdentityScopeRef.current === requestScope,
    );

  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId]);

  useEffect(() => {
    if (identityScopeRef.current === undefined) {
      identityScopeRef.current = identityScope;
      return;
    }
    if (identityScopeRef.current !== identityScope) {
      identityScopeRef.current = identityScope;
      detailsSessionGenerationRef.current += 1;
      deleteRequestGenerationRef.current += 1;
      deleteSessionRef.current = null;
      deleteSubmissionRef.current = null;
      deleteRestoreFocusRef.current = false;
      editSubmissionRef.current = false;
      memberSubmissionRef.current = null;
      selectedProjectIdRef.current = null;
      setSelectedProjectId(null);
      setSelectedProjectScope(null);
      setIsEditingProject(false);
      setEditProjectPending(false);
      setMemberDialogOpen(false);
      setAccessLevelDialogOpen(false);
      setProjectDeleteDialogOpen(false);
      setDeleteConfirmOpen(false);
      setSelectedMember(null);
      setProjectToDelete(null);
      setDependencies(null);
      setForceDelete(false);
      setDeleteId(null);
      setDependenciesLoading(false);
      setDeleteDependencyError("");
      setDeleteMutationError("");
      setIsSubmitting(false);
      resetMemberState();
    }
  }, [identityScope, resetMemberState]);

  useEffect(() => {
    if (selectedProjectId && projectsQuery.isSuccess && !selectedProject) {
      detailsSessionGenerationRef.current += 1;
      deleteRequestGenerationRef.current += 1;
      deleteSessionRef.current = null;
      deleteSubmissionRef.current = null;
      deleteRestoreFocusRef.current = false;
      editSubmissionRef.current = false;
      memberSubmissionRef.current = null;
      selectedProjectIdRef.current = null;
      setSelectedProjectId(null);
      setSelectedProjectScope(null);
      setIsEditingProject(false);
      setEditProjectPending(false);
      setMemberDialogOpen(false);
      setAccessLevelDialogOpen(false);
      setProjectDeleteDialogOpen(false);
      setDeleteConfirmOpen(false);
      setSelectedMember(null);
      setProjectToDelete(null);
      setDependencies(null);
      setForceDelete(false);
      setDeleteId(null);
      setDependenciesLoading(false);
      setDeleteDependencyError("");
      setDeleteMutationError("");
      setIsSubmitting(false);
      resetMemberState();
    }
  }, [
    projectsQuery.isSuccess,
    resetMemberState,
    selectedProject,
    selectedProjectId,
  ]);

  // Fetch project members when a canonical project and page are selected.
  useEffect(() => {
    if (!selectedProjectId || !selectedProject) return undefined;
    fetchProjectMembers();
    return invalidateMemberRequests;
  }, [
    fetchProjectMembers,
    invalidateMemberRequests,
    membersPagination.page,
    selectedProject,
    selectedProjectId,
  ]);

  useEffect(() => invalidateMemberRequests, [invalidateMemberRequests]);

  const handlePageChange = (event, newPage) => {
    setMembersPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleCreateProject = (event) => {
    if (event?.currentTarget instanceof HTMLElement) {
      createProjectTriggerRef.current = event.currentTarget;
    }
    setCreateFormOpen(true);
  };

  const handleCreateFormClose = () => {
    setCreateFormOpen(false);
  };

  const handleProjectClick = (
    project,
    triggerElement,
    startEditing = false,
  ) => {
    detailsSessionGenerationRef.current += 1;
    deleteRequestGenerationRef.current += 1;
    deleteSessionRef.current = null;
    deleteSubmissionRef.current = null;
    deleteRestoreFocusRef.current = false;
    editSubmissionRef.current = false;
    setDetailsTriggerElement(triggerElement || null);
    selectedProjectIdRef.current = project.id;
    setSelectedProjectId(project.id);
    setSelectedProjectScope(identityScope);
    setIsEditingProject(startEditing);
    setEditProjectPending(false);
    setEditProjectName(project.name);
    setEditProjectDescription(project.description || "");
    setEditProjectError("");
    setDeleteId(null);
    setDependenciesLoading(false);
    setProjectToDelete(null);
    setDependencies(null);
    setForceDelete(false);
    setDeleteDependencyError("");
    setDeleteMutationError("");
    setProjectDeleteDialogOpen(false);
    setIsSubmitting(false);
    // Reset pagination and search when viewing a project
    resetMemberState();
    setSearchQuery("");
  };

  const handleBackToProjects = () => {
    detailsSessionGenerationRef.current += 1;
    deleteRequestGenerationRef.current += 1;
    deleteSessionRef.current = null;
    deleteSubmissionRef.current = null;
    deleteRestoreFocusRef.current = false;
    editSubmissionRef.current = false;
    selectedProjectIdRef.current = null;
    setSelectedProjectId(null);
    setSelectedProjectScope(null);
    setIsEditingProject(false);
    setEditProjectPending(false);
    setEditProjectError("");
    setProjectToDelete(null);
    setProjectDeleteDialogOpen(false);
    setDependencies(null);
    setDeleteId(null);
    setDependenciesLoading(false);
    setDeleteDependencyError("");
    setDeleteMutationError("");
    setIsSubmitting(false);
    resetMemberState();
  };

  const handleEditProject = (e, project) => {
    e.stopPropagation();
    handleProjectClick(project, e.currentTarget, true);
  };

  const handleEditProjectSave = async () => {
    if (
      !selectedProject ||
      !editProjectName.trim() ||
      editSubmissionRef.current
    ) {
      return;
    }
    const projectId = selectedProject.id;
    const requestScope = selectedProjectScope;
    const sessionGeneration = detailsSessionGenerationRef.current;
    const isCurrentSession = () =>
      detailsSessionGenerationRef.current === sessionGeneration &&
      selectedProjectIdRef.current === projectId &&
      activeIdentityScopeRef.current === requestScope;

    editSubmissionRef.current = true;
    setEditProjectPending(true);
    setEditProjectError("");
    try {
      await updateProject({
        projectId,
        name: editProjectName,
        description: editProjectDescription,
      });
      if (isCurrentSession()) setIsEditingProject(false);
    } catch (error) {
      if (!isCurrentSession()) return;
      console.error("Error updating project:", error);
      setEditProjectError(error.message || t("projects.failedToUpdateProject"));
    } finally {
      if (isCurrentSession()) {
        editSubmissionRef.current = false;
        setEditProjectPending(false);
      }
    }
  };

  const handleEditProjectClose = () => {
    if (editProjectPending) return;
    setIsEditingProject(false);
    setEditProjectName(selectedProject?.name || "");
    setEditProjectDescription(selectedProject?.description || "");
    setEditProjectError("");
  };

  const isCurrentDeleteSession = useCallback((session, requestGeneration) => {
    if (
      !session ||
      deleteSessionRef.current !== session ||
      deleteRequestGenerationRef.current !== requestGeneration ||
      activeIdentityScopeRef.current !== session.requestScope
    ) {
      return false;
    }

    return (
      !session.startedFromDetails ||
      (detailsSessionGenerationRef.current ===
        session.detailsSessionGeneration &&
        selectedProjectIdRef.current === session.projectId)
    );
  }, []);

  const loadProjectDependencies = useCallback(
    async (session) => {
      if (!session?.projectId || deleteSessionRef.current !== session) return;

      const requestGeneration = deleteRequestGenerationRef.current + 1;
      deleteRequestGenerationRef.current = requestGeneration;
      setDeleteId(session.projectId);
      setDependencies(null);
      setDeleteDependencyError("");
      setDeleteMutationError("");
      setDependenciesLoading(true);

      try {
        const projectDependencies = await fetchProjectDependencies(
          session.projectId,
        );
        if (!isCurrentDeleteSession(session, requestGeneration)) return;
        if (!isValidProjectDependencies(projectDependencies)) {
          throw new Error("Invalid project dependency response");
        }
        setDependencies(projectDependencies);
      } catch (error) {
        if (!isCurrentDeleteSession(session, requestGeneration)) return;
        console.error("Failed to fetch project dependencies:", error);
        setDeleteDependencyError(
          error.message || "Failed to fetch project dependencies",
        );
      } finally {
        if (isCurrentDeleteSession(session, requestGeneration)) {
          setDeleteId(null);
          setDependenciesLoading(false);
        }
      }
    },
    [fetchProjectDependencies, isCurrentDeleteSession],
  );

  // Enhanced delete project handler with dependency checking
  const handleDeleteProject = (event, project, explicitTrigger = null) => {
    event?.stopPropagation?.();
    if (!project?.id || !canManageProject(project)) return;

    const trigger =
      explicitTrigger ||
      (event?.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : null);
    const session = {
      projectId: project.id,
      requestScope: identityScope,
      detailsSessionGeneration: detailsSessionGenerationRef.current,
      startedFromDetails: selectedProjectIdRef.current === project.id,
    };

    deleteSessionRef.current = session;
    deleteTriggerRef.current = trigger;
    setDeleteFallbackFocusElement(
      session.startedFromDetails
        ? detailsTriggerElement
        : projectsListFocusRef.current,
    );
    deleteRestoreFocusRef.current = true;
    deleteSubmissionRef.current = null;
    setSelectedMember(null);
    setProjectToDelete(project);
    setForceDelete(false);
    setDependencies(null);
    setDeleteDependencyError("");
    setDeleteMutationError("");
    setProjectDeleteDialogOpen(true);
    void loadProjectDependencies(session);
  };

  const handleRetryProjectDependencies = () => {
    const session = deleteSessionRef.current;
    if (!session || dependenciesLoading || isSubmitting) return;
    void loadProjectDependencies(session);
  };

  const handleConfirmProjectDelete = async () => {
    const session = deleteSessionRef.current;
    if (
      !projectToDelete ||
      !session ||
      projectToDelete.id !== session.projectId ||
      !isValidProjectDependencies(dependencies) ||
      dependenciesLoading ||
      deleteSubmissionRef.current ||
      (dependencies.has_dependencies && !forceDelete)
    ) {
      return;
    }

    const requestGeneration = deleteRequestGenerationRef.current;
    if (!isCurrentDeleteSession(session, requestGeneration)) return;

    const currentProject = canonicalProjectsRef.current.find(
      (project) => project.id === session.projectId,
    );
    if (!currentProject || !canManageProject(currentProject)) {
      setDeleteMutationError(t("projects.failedToDeleteProject"));
      return;
    }

    const submissionToken = Symbol("project-delete");
    deleteSubmissionRef.current = submissionToken;
    setDeleteMutationError("");
    setIsSubmitting(true);
    try {
      await deleteProject({
        projectId: session.projectId,
        permanent: false,
        force: forceDelete,
      });
      if (!isCurrentDeleteSession(session, requestGeneration)) return;

      deleteRestoreFocusRef.current = false;
      setProjectDeleteDialogOpen(false);
      setProjectToDelete(null);
      setDependencies(null);
      setIsSubmitting(false);
      deleteSessionRef.current = null;

      // If the deleted project was selected, go back to projects list
      if (selectedProjectIdRef.current === session.projectId) {
        detailsSessionGenerationRef.current += 1;
        selectedProjectIdRef.current = null;
        setSelectedProjectId(null);
        setSelectedProjectScope(null);
        resetMemberState();
      }
    } catch (error) {
      if (!isCurrentDeleteSession(session, requestGeneration)) return;
      console.error("Error deleting project:", error);
      setDeleteMutationError(error.message || "Failed to delete project");
    } finally {
      if (deleteSubmissionRef.current === submissionToken) {
        deleteSubmissionRef.current = null;
        if (isCurrentDeleteSession(session, requestGeneration)) {
          setIsSubmitting(false);
        }
      }
    }
  };

  const handleCancelProjectDelete = () => {
    if (isSubmitting) return;
    deleteRequestGenerationRef.current += 1;
    deleteSessionRef.current = null;
    deleteSubmissionRef.current = null;
    setProjectDeleteDialogOpen(false);
    setProjectToDelete(null);
    setDependencies(null);
    setForceDelete(false);
    setDeleteDependencyError("");
    setDeleteMutationError("");
    setDeleteId(null);
    setDependenciesLoading(false);
  };

  // Member management handlers
  const handleAddMember = () => {
    if (
      isSubmitting ||
      !canManageSelectedProjectMembers ||
      !isCurrentMemberSession(selectedProject?.id, selectedProjectScope)
    ) {
      return;
    }
    setMemberDialogOpen(true);
    restoreAddMemberFocusRef.current = true;
  };

  const handleMemberDialogClose = () => {
    if (isSubmitting || memberSubmissionRef.current) return;
    setMemberDialogOpen(false);
    setNewMemberEmail("");
    setNewMemberAccessLevel("viewer");
    setErrorMessage("");
  };

  const handleChangeAccessLevel = (memberId) => {
    const member = members.find(
      (item) => (item.id || item.user_id) === memberId,
    );
    if (
      !member ||
      !canChangeMemberAccess(member) ||
      !isCurrentMemberSession(selectedProject?.id, selectedProjectScope)
    ) {
      return;
    }
    setSelectedMember(member);
    // Convert API level to UI level for the form
    accessSessionRef.current = {};
    restoreAccessMemberFocusRef.current = memberId;
    setErrorMessage("");
    const uiAccessLevel = mapApiAccessLevelToUI(member.access_level);
    setNewAccessLevel(uiAccessLevel);
    setAccessLevelDialogOpen(true);
  };

  const handleAccessLevelDialogClose = () => {
    if (isSubmitting || memberSubmissionRef.current) return;
    accessSessionRef.current = null;
    setAccessLevelDialogOpen(false);
    setSelectedMember(null);
    setErrorMessage("");
  };

  const handleAccessLevelSave = async () => {
    if (
      isSubmitting ||
      memberSubmissionRef.current ||
      !selectedMember ||
      !selectedProject ||
      !canChangeMemberAccess(selectedMember) ||
      !isCurrentMemberSession(selectedProject.id, selectedProjectScope)
    ) {
      return;
    }

    const currentProject = canonicalProjectsRef.current.find((project) => project.id === selectedProject.id);
    const currentMember = members.find((member) => (member.id || member.user_id) === (selectedMember.id || selectedMember.user_id));
    const accessSession = accessSessionRef.current;
    if (!accessSession || !currentProject || !(canManageProject(currentProject) || isOrgOwner) ||
      !currentMember || membersLoading || membersError || !canChangeMemberAccess(currentMember) ||
      !["admin", "editor", "modifier", "viewer"].includes(newAccessLevel)) return;

    const projectId = selectedProject.id;
    const requestScope = selectedProjectScope;
    const sessionGeneration = detailsSessionGenerationRef.current;
    const submissionToken = {};
    memberSubmissionRef.current = submissionToken;
    const isCurrentSession = () =>
      accessSessionRef.current === accessSession &&
      detailsSessionGenerationRef.current === sessionGeneration &&
      isCurrentMemberSession(projectId, requestScope);

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      // Map editor to modifier for the API call
      const apiAccessLevel = mapUIAccessLevelToAPI(newAccessLevel);

      // Use shareId if available, otherwise use user_id
      const shareId = currentMember.id || currentMember.user_id;

      await updateShareAccess(projectId, shareId, apiAccessLevel);
      if (!isCurrentSession()) return;

      // Refresh the members list after updating
      fetchProjectMembers();

      setAccessLevelDialogOpen(false);
      setSelectedMember(null);
    } catch (error) {
      if (!isCurrentSession()) return;
      console.error("Error updating access level:", error);
      setErrorMessage(error.message || "Failed to update access level");
    } finally {
      if (memberSubmissionRef.current === submissionToken) {
        memberSubmissionRef.current = null;
        if (isCurrentSession()) setIsSubmitting(false);
      }
    }
  };

  const handleDeleteMember = (memberId) => {
    const member = members.find(
      (item) => (item.id || item.user_id) === memberId,
    );
    if (
      isSubmitting || memberSubmissionRef.current ||
      !member ||
      !selectedProject ||
      !canRemoveMember(member) ||
      !isCurrentMemberSession(selectedProject.id, selectedProjectScope)
    ) {
      return;
    }
    setSelectedMember(member);
    removeMemberSessionRef.current = {};
    restoreRemoveMemberFocusRef.current = memberId;
    setErrorMessage("");
    // Show confirmation dialog before deleting
    setDeleteConfirmOpen(true);
  };

  const handleRemoveMemberClose = () => {
    if (isSubmitting || memberSubmissionRef.current) return;
    removeMemberSessionRef.current = null;
    setDeleteConfirmOpen(false);
    setSelectedMember(null);
    setErrorMessage("");
  };

  const handleConfirmDeleteMember = async () => {
    if (
      isSubmitting ||
      memberSubmissionRef.current ||
      !selectedMember ||
      !selectedProject ||
      !canRemoveMember(selectedMember) ||
      !isCurrentMemberSession(selectedProject.id, selectedProjectScope)
    ) {
      return;
    }

    const currentProject = canonicalProjectsRef.current.find((project) => project.id === selectedProject.id);
    const currentMember = members.find((member) => (member.id || member.user_id) === (selectedMember.id || selectedMember.user_id));
    const session = removeMemberSessionRef.current;
    if (!session || !currentProject || !(canManageProject(currentProject) || isOrgOwner) ||
      !currentMember || membersLoading || membersError || !canRemoveMember(currentMember) ||
      !isSameUserIdentity(currentMember, { id: selectedMember.user_id, email: selectedMember.email })) return;

    const projectId = selectedProject.id;
    const requestScope = selectedProjectScope;
    const sessionGeneration = detailsSessionGenerationRef.current;
    const submissionToken = {};
    memberSubmissionRef.current = submissionToken;
    const isCurrentSession = () =>
      removeMemberSessionRef.current === session &&
      detailsSessionGenerationRef.current === sessionGeneration &&
      isCurrentMemberSession(projectId, requestScope);

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      // Use shareId if available, otherwise use user_id
      const shareId = currentMember.id || currentMember.user_id;

      // Call API to remove share
      await projectsApi.deleteProjectShare(projectId, shareId);
      if (!isCurrentSession()) return;

      // Refresh members list after removing
      fetchProjectMembers();

      // Close dialogs
      setDeleteConfirmOpen(false);
      setSelectedMember(null);
    } catch (error) {
      if (!isCurrentSession()) return;
      console.error("Error removing team member:", error);
      setErrorMessage(error.message || "Failed to remove team member");
    } finally {
      if (memberSubmissionRef.current === submissionToken) {
        memberSubmissionRef.current = null;
        if (isCurrentSession()) setIsSubmitting(false);
      }
    }
  };

  const handleAddNewMember = async () => {
    if (
      isSubmitting ||
      memberSubmissionRef.current ||
      !canManageSelectedProjectMembers ||
      !selectedProject ||
      !isCurrentMemberSession(selectedProject.id, selectedProjectScope)
    ) {
      return;
    }

    const projectId = selectedProject.id;
    const requestScope = selectedProjectScope;
    const sessionGeneration = detailsSessionGenerationRef.current;
    const isCurrentSession = () =>
      detailsSessionGenerationRef.current === sessionGeneration &&
      isCurrentMemberSession(projectId, requestScope);

    const currentProject = canonicalProjectsRef.current.find((project) => project.id === projectId);
    if (!currentProject || !(canManageProject(currentProject) || isOrgOwner) ||
      !["admin", "editor", "modifier", "viewer"].includes(newMemberAccessLevel)) return;

    const trimmedEmail = newMemberEmail.trim();

    if (!trimmedEmail) {
      setErrorMessage(t("shareDialog.emailRequired"));
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setErrorMessage(t("shareDialog.validEmailRequired"));
      return;
    }

    const submissionToken = {};
    memberSubmissionRef.current = submissionToken;
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      // Map editor to modifier for the API call
      const apiAccessLevel = mapUIAccessLevelToAPI(newMemberAccessLevel);

      // Call the shareProject API with separate parameters for email and accessLevel
      // NOT passing a single object with email and access_level properties
      await shareProject(projectId, trimmedEmail, apiAccessLevel);
      if (!isCurrentSession()) return;

      // Refresh members after adding
      fetchProjectMembers();

      // Close the dialog
      setMemberDialogOpen(false);
      setNewMemberEmail("");
      setNewMemberAccessLevel("viewer");
      setErrorMessage("");
    } catch (error) {
      if (!isCurrentSession()) return;
      console.error("Error sharing project:", error);
      setErrorMessage(error.message || "Failed to add team member");
    } finally {
      if (memberSubmissionRef.current === submissionToken) {
        memberSubmissionRef.current = null;
        if (isCurrentSession()) setIsSubmitting(false);
      }
    }
  };

  // Filter projects based on search query
  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Filter members based on search query
  const filteredMembers =
    searchQuery && members.length > 0
      ? members.filter(
          (member) =>
            (member.email &&
              member.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (member.first_name &&
              member.first_name
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (member.last_name &&
              member.last_name
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (member.username &&
              member.username
                .toLowerCase()
                .includes(searchQuery.toLowerCase())),
        )
      : members;

  const detailsMembers = filteredMembers.map((member) => {
    const displayName =
      member.first_name && member.last_name
        ? `${member.first_name} ${member.last_name}`
        : member.username || member.email || t("common.unknown");
    const initialsSource = [member.first_name, member.last_name]
      .filter(Boolean)
      .map((name) => name.charAt(0))
      .join("");

    return {
      id: member.id || member.user_id,
      displayName,
      email: member.email || "",
      accessLabel: getAccessLevelLabel(member.access_level, t),
      initials: (initialsSource || displayName.charAt(0) || "?")
        .slice(0, 2)
        .toUpperCase(),
      isOwner: Boolean(member.is_owner),
      canManage:
        canChangeMemberAccess(member) && canRemoveMember(member),
    };
  });

  const legacyModalOpen = Boolean(
    memberDialogOpen ||
      accessLevelDialogOpen ||
      deleteConfirmOpen ||
      projectDeleteDialogOpen,
  );
  const detailsSheetOpen = Boolean(selectedProject) && !legacyModalOpen;

  return (
    <>
      <div className="mt-0 flex h-full flex-col">
        {/* Search and Filter Bar */}
        <div className="mb-4 flex min-w-0 items-center justify-between gap-3 px-6 pt-8">
          <div className="relative min-w-0 flex-1 sm:max-w-[300px]">
            <Search
              className={recordTable.searchIcon}
              aria-hidden="true"
            />
            <Input
              ref={projectsListFocusRef}
              type="search"
              placeholder={t("projects.searchProjects")}
              value={searchQuery}
              onChange={handleSearchChange}
              className={recordTable.search}
            />
          </div>
          <ShadcnButton
            type="button"
            onClick={handleCreateProject}
            className="min-w-[134px]"
          >
            <Plus aria-hidden="true" />
            {t("projects.newProject")}
          </ShadcnButton>
        </div>

        {/* Content Area */}
        <div className={cn(recordTable.container, "min-h-0 flex-1 overflow-y-auto px-6 pb-8")}>
          <table className={cn(recordTable.table, "min-w-[650px]")}>
            <thead>
              <tr className={recordTable.header}>
                <th
                  scope="col"
                  className={recordTable.headerCell}
                >
                  {t("common.project")}
                </th>
                <th
                  scope="col"
                  className={cn(recordTable.headerCell, "w-40")}
                >
                  {t("projects.members")}
                </th>
                <th
                  scope="col"
                  className={cn(recordTable.headerCell, "w-48")}
                >
                  {t("common.created")}
                </th>
                <th
                  scope="col"
                  className={cn(recordTable.headerCell, "w-[100px] text-end")}
                >
                  <span className="sr-only">{t("common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => (
                <tr
                  key={project.id}
                  className={cn(recordTable.row, "cursor-pointer")}
                  onClick={(event) =>
                    handleProjectClick(
                      project,
                      event.currentTarget.querySelector("button"),
                    )
                  }
                >
                  <th
                    scope="row"
                    className={recordTable.cell}
                  >
                    <button
                      type="button"
                      data-project-scope={identityScope ?? undefined}
                      aria-label={t("projects.projectLabel", {
                        name: project.name,
                      })}
                      className="block w-full rounded-sm text-start outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleProjectClick(project, event.currentTarget);
                      }}
                    >
                      <span className={recordTable.primary}>
                        {project.name}
                      </span>
                      <span className={recordTable.secondary}>
                        {project.description ||
                          t("projects.noDescriptionAvailable")}
                      </span>
                    </button>
                  </th>
                  <td className={recordTable.cell}>
                    {/* We no longer have accurate count without fetching, so use a placeholder */}
                    - {t("projects.members")}
                  </td>
                  <td className={recordTable.cell}>
                    {project.createdAt
                      ? new Date(project.createdAt).toLocaleDateString(
                          getIntlLocale(i18n.language),
                        )
                      : "N/A"}
                  </td>
                  <td className={cn(recordTable.cell, "text-end")}>
                    {canManageProject(project) && (
                      <div className={recordTable.actions}>
                        <ShadcnButton
                          type="button"
                          variant="ghost"
                          size="icon"
                          data-project-scope={identityScope ?? undefined}
                          aria-label={t("projects.editProject")}
                          title={t("projects.editProject")}
                          className={recordTable.action}
                          onClick={(event) => handleEditProject(event, project)}
                        >
                          <Pencil aria-hidden="true" />
                        </ShadcnButton>
                        <ShadcnButton
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t("projects.deleteProject")}
                          title={t("projects.deleteProject")}
                          className={cn(
                            recordTable.action,
                            "text-destructive hover:bg-destructive/10 hover:text-destructive",
                          )}
                          onClick={(event) =>
                            handleDeleteProject(event, project)
                          }
                        >
                          {deleteId === project.id ? (
                            <LoaderCircle
                              className="animate-spin"
                              aria-hidden="true"
                            />
                          ) : (
                            <Trash2 aria-hidden="true" />
                          )}
                        </ShadcnButton>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filteredProjects.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className={cn(recordTable.cell, "py-6 text-center")}
                  >
                    <p className="text-sm text-muted-foreground">
                      {t("projects.noProjectsFound")}
                    </p>
                    <ShadcnButton
                      type="button"
                      variant="outline"
                      onClick={handleCreateProject}
                      className="mt-4"
                    >
                      <Plus aria-hidden="true" />
                      {t("projects.createNewProject")}
                    </ShadcnButton>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedProject ? (
        <ProjectDetailsSheet
          open={detailsSheetOpen}
          project={selectedProject}
          isRTL={isRTL}
          canEditProject={canManageProject(selectedProject)}
          canManageMembers={canManageSelectedProjectMembers}
          isEditing={isEditingProject}
          editName={editProjectName}
          editDescription={editProjectDescription}
          editError={editProjectError}
          editPending={editProjectPending}
          deletePending={deleteId === selectedProject.id}
          members={detailsMembers}
          membersLoading={membersLoading}
          membersError={membersError}
          memberSearch={searchQuery}
          pagination={membersPagination}
          restoreFocusElement={detailsTriggerElement}
          restoreFocusScope={selectedProjectScope}
          suppressFocusRestore={legacyModalOpen}
          restoreAddMemberFocusRef={restoreAddMemberFocusRef}
          restoreAccessMemberFocusRef={restoreAccessMemberFocusRef}
          restoreRemoveMemberFocusRef={restoreRemoveMemberFocusRef}
          onClose={handleBackToProjects}
          onStartEdit={() => {
            setEditProjectName(selectedProject.name);
            setEditProjectDescription(selectedProject.description || "");
            setEditProjectError("");
            setIsEditingProject(true);
          }}
          onCancelEdit={handleEditProjectClose}
          onEditNameChange={setEditProjectName}
          onEditDescriptionChange={setEditProjectDescription}
          onSubmitEdit={handleEditProjectSave}
          onMemberSearchChange={setSearchQuery}
          onRetryMembers={fetchProjectMembers}
          onPreviousPage={() =>
            handlePageChange(null, membersPagination.page - 1)
          }
          onNextPage={() => handlePageChange(null, membersPagination.page + 1)}
          onAddMember={handleAddMember}
          onChangeMemberAccess={handleChangeAccessLevel}
          onRemoveMember={handleDeleteMember}
          onDeleteProject={(triggerElement) =>
            handleDeleteProject(null, selectedProject, triggerElement)
          }
        />
      ) : null}
      <ProjectDeleteDialog
        open={projectDeleteDialogOpen && Boolean(projectToDelete)}
        projectName={projectToDelete?.name}
        dependencies={dependencies}
        dependenciesLoading={dependenciesLoading}
        dependencyError={deleteDependencyError}
        mutationError={deleteMutationError}
        forceDelete={forceDelete}
        pending={isSubmitting}
        returnFocusRef={deleteTriggerRef}
        fallbackFocusElement={deleteFallbackFocusElement}
        restoreFocusRef={deleteRestoreFocusRef}
        onCancel={handleCancelProjectDelete}
        onRetry={handleRetryProjectDependencies}
        onForceDeleteChange={setForceDelete}
        onConfirm={handleConfirmProjectDelete}
      />

      <ProjectChangeAccessDialog
        open={accessLevelDialogOpen && Boolean(selectedProject)}
        memberName={selectedMember?.first_name && selectedMember?.last_name
          ? `${selectedMember.first_name} ${selectedMember.last_name}`
          : selectedMember?.email || t("projects.thisUser")}
        role={newAccessLevel}
        pending={isSubmitting}
        error={errorMessage}
        onRoleChange={setNewAccessLevel}
        onCancel={handleAccessLevelDialogClose}
        onSave={handleAccessLevelSave}
      />

      <ProjectAddMemberDialog
        open={memberDialogOpen && Boolean(selectedProject)}
        email={newMemberEmail}
        role={newMemberAccessLevel}
        error={errorMessage}
        pending={isSubmitting}
        onCancel={handleMemberDialogClose}
        onSubmit={handleAddNewMember}
        onEmailChange={(value) => { setNewMemberEmail(value); setErrorMessage(""); }}
        onRoleChange={setNewMemberAccessLevel}
      />

      <ProjectRemoveMemberDialog
        open={Boolean(deleteConfirmOpen && !projectToDelete && selectedMember && selectedProject)}
        memberName={selectedMember?.first_name && selectedMember?.last_name
          ? `${selectedMember.first_name} ${selectedMember.last_name}`
          : selectedMember?.email || t("projects.thisUser")}
        pending={isSubmitting}
        error={errorMessage}
        onCancel={handleRemoveMemberClose}
        onConfirm={handleConfirmDeleteMember}
      />

      {/* Create Project Form Dialog */}
      <CreateProjectForm
        open={createFormOpen}
        onClose={handleCreateFormClose}
        onCreateProject={createProject}
        returnFocusRef={createProjectTriggerRef}
      />
    </>
  );
};

export default ProjectsManagement;
